import {ColumnDef} from "@tanstack/react-table"
import {Archive, Coins} from "lucide-react"
import {sortableColumnHeader} from "@/components/data-table.tsx"
import {Link} from "@/components/tailwind/link.tsx"
import {Text} from "@/components/tailwind/text"
import {FormerMember} from "@/models/former-member.ts"
import {useReportsTranslation} from "@/i18n/hooks"

function PointsStatementCell({member}: {member: FormerMember}) {
    const {t} = useReportsTranslation()

    return (
        <Link
            href={`/members/${member.id}/points-statement?from=former-members`}
            data-testid={`former-member-points-statement-link-${member.id}`}
            title={t('formerMembers.table.pointsStatementLink', {memberNo: member.memberNo})}
            aria-label={t('formerMembers.table.pointsStatementLink', {memberNo: member.memberNo})}
        >
            <Coins className="h-4 w-4"/>
        </Link>
    )
}

function RetainedDataCell({member}: {member: FormerMember}) {
    const {t} = useReportsTranslation()

    return (
        <Link
            href={`/members/${member.id}/retained-data`}
            data-testid={`former-member-retained-data-link-${member.id}`}
            title={t('formerMembers.table.retainedDataLink', {memberNo: member.memberNo})}
            aria-label={t('formerMembers.table.retainedDataLink', {memberNo: member.memberNo})}
        >
            <Archive className="h-4 w-4"/>
        </Link>
    )
}

export const formerMemberColumns: ColumnDef<FormerMember>[] = [
    {
        accessorKey: "memberNo",
        header: ({column}) =>
            sortableColumnHeader(column, "formerMembers.table.headers.memberNo", "reports"),
    },
    {
        accessorKey: "lastName",
        header: ({column}) =>
            sortableColumnHeader(column, "formerMembers.table.headers.lastName", "reports"),
    },
    {
        accessorKey: "firstName",
        header: ({column}) =>
            sortableColumnHeader(column, "formerMembers.table.headers.firstName", "reports"),
    },
    {
        id: "address",
        accessorFn: (row) => `${row.street} ${row.houseNumber ?? ''} ${row.postcode} ${row.city}`.trim(),
        header: ({column}) =>
            sortableColumnHeader(column, "formerMembers.table.headers.address", "reports"),
        cell: ({row}) => {
            const member = row.original
            return (
                <address>
                    <Text>{member.street} {member.houseNumber}</Text>
                    <Text>{member.postcode} {member.city}</Text>
                </address>
            )
        },
    },
    {
        accessorKey: "membershipEnd",
        header: ({column}) =>
            sortableColumnHeader(column, "formerMembers.table.headers.membershipEnd", "reports"),
        cell: ({row}) => {
            const date = row.original.membershipEnd
            if (!date) return <Text>-</Text>
            return <Text>{new Date(date).toLocaleDateString('de-DE')}</Text>
        },
    },
    {
        accessorKey: "resignationReason",
        header: ({column}) =>
            sortableColumnHeader(column, "formerMembers.table.headers.reason", "reports"),
        cell: ({row}) => <Text>{row.original.resignationReason ?? "-"}</Text>,
    },
    {
        id: "pointsStatement",
        cell: ({row}) => <PointsStatementCell member={row.original}/>,
    },
    {
        id: "retainedData",
        cell: ({row}) => <RetainedDataCell member={row.original}/>,
    },
]
